import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GisDimsMapComponent } from 'src/app/main/shared/gis-dims-map/gis-dims-map.component';
import { InfomodalComponent } from 'src/app/main/shared/infomodal/infomodal.component';
import { DataService } from 'src/app/main/shared/services/data.service';
import { UtilsService } from 'src/app/utils/utils.service';
import { isNullOrUndefined } from 'util';
import { SeamlessTempDataService } from '../seamless-temp-data.service';

@Component({
  selector: 'app-seamless-temp-sidebar',
  templateUrl: './seamless-temp-sidebar.component.html',
  styleUrls: ['./seamless-temp-sidebar.component.scss']
})
export class SeamlessTempSidebarComponent implements OnInit {

  @Input() labelStrings: any;
  @Input() blockNum: number;
  @Output() playPos = new EventEmitter<any>();
  selected: string;
  queryParams: {};
  isInitialLoad: boolean = false;
  dateIndexLength: number = 7;
  selectedSource: any = 'IMD';
  isDistrictChecked: boolean = false
  activeRadioList: boolean[] = [];
  modalRef: any;
  aggr: any = { value: 'mean', required: true };
  sourceOptions: any = ['IMD', 'ECMWF', 'IMD(WRF + GFS)'];
  sourceOptions7W: any = ['EUMETSAT'];
  sourceOptions24H: any = ['EUMETSAT'];
  sourceOptionsSeasonal: any = ['ECMWF'];
  moduleName: string;
  sourceMapping = {
    'IMD': 'IMD_LONG_RANGE',
    'ECMWF': 'ECMWF',
    'IMD(WRF + GFS)': 'IMD_SHORT_RANGE',
    'EUMETSAT': 'EUMETSAT_SURFACE_TEMPERATURE',
    'MODIS': 'MODIS_LST_DAILY'
  }
  destroy: any = new Subject();
  subscriptions: any = [];
  sessionStorage: any = {
    params: {}
  };
  selectedFormat: any = 'DAILY';
  sourceOptions7Day: any = ['EUMETSAT', 'MODIS'];
  forecastLength: any;
  hourlyTimestampMap = {};
  selectedTimestamp: any;
  airTemperatureOn: boolean = false;
  private prevForecastLength: any;

  constructor(private router: Router,
    private gisUtils: UtilsService,
    private modalService: NgbModal,
    private dataService: DataService,
    private gisMapComp: GisDimsMapComponent,
    private seamlessDataSrv: SeamlessTempDataService) {
    this.moduleName = this.router.url.split('/')[1];
    this.selected = this.router.url.split("/")[2]
  }

  ngOnInit() {
    this.isInitialLoad = true;
    this.airTemperatureOn = false;
    this.gisUtils.airTemperatureToggle.next(false);
    this.subscribeEvents()
    this.setParams()
  }

  subscribeEvents() {
    const selectedParamsRef = this.gisUtils.selectedParams.pipe(takeUntil(this.destroy)).subscribe((params: any) => {
      let sessionStorageSelectedParams: any = sessionStorage.getItem('params');
      if (Object.keys(params).length > 0) {
        this.queryParams = params;
      } else {
        this.queryParams = sessionStorageSelectedParams ? JSON.parse(sessionStorageSelectedParams) : {};
        this.sessionStorage.params = { ...this.queryParams };
        if (Object.keys(this.queryParams).length) {
          this.gisUtils.setSelectedParams(this.queryParams);
        }
      }
      if (!isNullOrUndefined(this.queryParams) && Object.keys(this.queryParams).length > 0) {
        const normalizedSource = this.queryParams['source'] || 'IMD_LONG_RANGE';
        this.forecastLength = this.queryParams['forecastLength'];
        this.selectedFormat = this.queryParams['format'];
        this.selectedTimestamp = this.queryParams['hourlyDate'];
        if (this.forecastLength === '-7W') {
          // Only -7W forces EUMETSAT
          this.selectedSource = 'EUMETSAT';
          this.selectedFormat =  'DAILY';
        } else if (this.forecastLength === '-7') {
          // For -7, resolve source from params (MODIS_LST_DAY/NIGHT → 'MODIS', EUMETSAT → 'EUMETSAT')
          if (normalizedSource === 'MODIS_LST_DAY' || normalizedSource === 'MODIS_LST_NIGHT') {
            this.selectedSource = 'MODIS';
            this.selectedFormat = normalizedSource === 'MODIS_LST_NIGHT' ? 'NIGHT' : 'DAY';
          } else if (normalizedSource === 'EUMETSAT_SURFACE_TEMPERATURE') {
            this.selectedSource = 'EUMETSAT';
            // Preserve DAILY/HOURLY from params; only default to DAILY for invalid/stale formats (e.g. DAY/NIGHT left over from MODIS)
            const incomingFormat = this.queryParams['format'];
            this.selectedFormat = (incomingFormat === 'DAILY' || incomingFormat === 'HOURLY') ? incomingFormat : 'DAILY';
          } else {
            this.selectedSource = 'EUMETSAT'; 
            this.selectedFormat='DAILY'; // safe default for -7
          }
        } else if (this.forecastLength === '-24') {
          // Only EUMETSAT is available for -24H observed data
          this.selectedSource = 'EUMETSAT';
          this.selectedFormat = 'DAILY';
        } else if (this.forecastLength === 'seasonal') {
          // Only ECMWF is available for +7M seasonal forecast
          this.selectedSource = 'ECMWF';
          this.selectedFormat = 'DAILY';
        } else {
          if (normalizedSource === 'IMD_SHORT_RANGE') {
            this.selectedSource = 'IMD(WRF + GFS)';
          } else if (normalizedSource === 'ECMWF') {
            this.selectedSource = 'ECMWF';
          } else {
            this.selectedSource = 'IMD'; // covers IMD_LONG_RANGE + any stale EUMETSAT/MODIS
          }
          this.selectedFormat = 'DAILY';
        }
        // Sync aggr from params (e.g. when leaving -7W, routeChange resets aggrType to mean)
        if (this.queryParams['aggrType']) {
          this.aggr['value'] = this.queryParams['aggrType'];
        }
        this.initMethod();
        const resolvedSource = this.selectedSource === 'MODIS'
          ? (this.selectedFormat === 'NIGHT' ? 'MODIS_LST_NIGHT' : 'MODIS_LST_DAY')
          : (this.sourceMapping[this.selectedSource] || 'IMD_LONG_RANGE');

        if (!this.isInitialLoad && (this.queryParams['source'] !== resolvedSource || this.queryParams['format'] !== this.selectedFormat)) {
          this.requestSelectedFilterParams();
        }

        // Air Temperature toggle must reset to OFF on every time-toggle switch,
        // and whenever it becomes hidden (e.g. aggregation switched to Mean, format switched to Hourly).
        if (this.prevForecastLength !== undefined && this.prevForecastLength !== this.forecastLength) {
          this.airTemperatureOn = false;
          this.gisUtils.airTemperatureToggle.next(false);
        }
        if (!this.showAirTemperatureToggle() && this.airTemperatureOn) {
          this.airTemperatureOn = false;
          this.gisUtils.airTemperatureToggle.next(false);
        }
        this.prevForecastLength = this.forecastLength;
      }
    });
    this.subscriptions.push(selectedParamsRef)
  }


  setParams() {
    if (this.isInitialLoad && !Object.keys(this.sessionStorage.params).length) {
      this.requestSelectedFilterParams();
    }
    this.isInitialLoad = false;
  }

  initMethod() {
    const validOptions = this.getFilteredSourceOptions();
    if (!validOptions.includes(this.selectedSource)) {
      this.selectedSource = validOptions[0];
    }
    if (this.queryParams['forecastLength'] == '-7' && this.queryParams['format'] == 'HOURLY') {
      this.hourlyTimestampMap = this.seamlessDataSrv.getLast7DaysMap();
    }
  }

  timestampChange() {
    let options = { ...this.queryParams }
    options['hourlyDate'] = this.selectedTimestamp
    this.gisUtils.setSelectedParams(options)
  }

  buttonClicked(blockNumber, from?: string) {
    if (from === 'HTML') {
      this.playPos.emit(blockNumber);
    }
    if (this.activeRadioList[blockNumber]) {
      this.gisUtils.setIndex(this.labelStrings[blockNumber]['groupName']);
    }
  }

  validate(i) {
    if (this.labelStrings[i] == undefined || this.labelStrings[i] == null) {
      return ""
    }
    else {
      return this.labelStrings[i]['fullLabel'];
    }
  }

  sourceChange() {
    if (this.selectedSource === 'MODIS') {
      this.selectedFormat = 'DAY';
    } else {
      this.selectedFormat = 'DAILY';
    }
    this.requestSelectedFilterParams();
  }


  ngOnChanges(change: SimpleChanges) {
    if (!isNullOrUndefined(this.labelStrings) && this.labelStrings.length > 0) {
      let timeStamp = this.labelStrings[this.blockNum] ? this.labelStrings[this.blockNum].groupName : null;
      if (timeStamp) {
        this.gisUtils.setTimeStamp(timeStamp);
      }
      if (!isNullOrUndefined(change.blockNum) && Object.keys(this.labelStrings).length > 0) {
        if (change.blockNum.currentValue) {
          this.buttonClicked(change.blockNum.currentValue);
        }
      } else {
        this.buttonClicked(this.blockNum);
      }
      const len = this.activeRadioList.length;
      this.activeRadioList = [];
      for (let i = 0; i < this.dateIndexLength; ++i) {
        if (i === this.blockNum) {
          this.activeRadioList.push(true);
        } else {
          this.activeRadioList.push(false);
        }
      }
    }
  }

  requestSelectedFilterParams() {
    let currentUrlParams: any = {};
    if (!this.isInitialLoad) {
      currentUrlParams = {...this.queryParams};
    }
    currentUrlParams['component'] = 'SEAMLESSTEMP';
    if (this.selectedSource === 'MODIS') {
      currentUrlParams['source'] = this.selectedFormat === 'NIGHT'? 'MODIS_LST_NIGHT':'MODIS_LST_DAY';
    } else {
      currentUrlParams['source'] = this.sourceMapping[this.selectedSource];
    }
    currentUrlParams['aggrType'] = this.aggr['value']
    currentUrlParams['format'] = this.selectedFormat
    if (this.isInitialLoad) {
      currentUrlParams['actionType'] = 'filter';
      currentUrlParams['view'] = 'Admin';
      currentUrlParams['pType'] = 'COUNTRY';
      currentUrlParams['cType'] = 'STATE';
      currentUrlParams['parentLoc'] = 'COUNTRY';
      currentUrlParams['dashboardView'] = 'INDIA';
      currentUrlParams['dashboardType'] = 'gis';
      const userLocationConfig = this.dataService.formQuerParamsVariables(this.gisMapComp.metaInfo.userDetailsJson.data.location, this.gisMapComp.metaInfo.userDetailsJson.data.locType);
      const currentLocations: any = userLocationConfig.locationInfo[userLocationConfig.types.pType.toLowerCase()].split('##');
      currentUrlParams['pUUID'] = currentLocations[1]
      currentUrlParams['locName'] = currentLocations[0]
      currentUrlParams['locType'] = userLocationConfig.types.pType;
      currentUrlParams['pEUUID'] = userLocationConfig.locationInfo.country.split('##')[1];
      currentUrlParams['location'] = userLocationConfig.locationInfo.location;
      currentUrlParams['source'] = 'IMD_LONG_RANGE'
      currentUrlParams['forecastLength'] = 'seamless'
      currentUrlParams['date'] = this.seamlessDataSrv.getDateForSeamless()
      currentUrlParams['hourlyDate'] = this.seamlessDataSrv.getDateForHourlyData()
      this.isInitialLoad = false
    }
    this.gisUtils.setSelectedParams(currentUrlParams);
  }

  getFilteredSourceOptions(): any[] {
    if (this.forecastLength === '-7W') return this.sourceOptions7W;
    if (this.forecastLength === '-7') return this.sourceOptions7Day;
    if (this.forecastLength === '-24') return this.sourceOptions24H;
    if (this.forecastLength === 'seasonal') return this.sourceOptionsSeasonal;
    return this.sourceOptions;
  }
  getFormatOptions(): { [key: string]: string } {
    if (this.selectedSource === 'MODIS') {
      return { 'Day': 'DAY', 'Night': 'NIGHT' };
    }
    return { 'Daily': 'DAILY', 'Hourly': 'HOURLY' };
  }

  shouldShowLayers(): boolean {
    const forecast = Number(this.queryParams['forecastLength']);
    return forecast === 10;
  }

  infoclick(value) {
    const modalRef = this.modalService.open(InfomodalComponent, {
      backdrop: 'static',
      keyboard: true,
      windowClass: 'backgroundTransparent  infocomponent'
    });
    modalRef.componentInstance.selected = value;
  }

  sendPlayPos(event) {
    this.playPos.emit(event);
  }

  toggleDistrictLayer(event) {
    const checkbox = event.target as HTMLInputElement;
    this.isDistrictChecked = checkbox.checked;
    this.gisUtils.lstDistrictLayerToggle.next(this.isDistrictChecked)
  }

  formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = ("0" + (date.getMonth() + 1)).slice(-2);
    const d = ("0" + date.getDate()).slice(-2);
    return `${y}${m}${d}`;
  }

  setAggregation(aggr: string): void {
    const value = aggr || 'mean';
    this.aggr['value'] = value;
    this.requestSelectedFilterParams()
  }

  showAirTemperatureToggle(): boolean {
    if (this.forecastLength === '-24') return true;
    const aggregationVisible = (this.forecastLength === '-7' && this.selectedSource === 'EUMETSAT' && this.selectedFormat === 'DAILY') || this.forecastLength === '-7W';
    return aggregationVisible && this.aggr['value'] !== 'mean';
  }

  onAirTemperatureToggle(): void {
    this.gisUtils.airTemperatureToggle.next(this.airTemperatureOn);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

}
